import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { useTurmas } from '../hooks/useTurmas';

interface CriancaDia {
  id_aluno: number;
  nome: string;
  rotina_id: number | null;
  alimentacao: any[];
  higiene: any[];
  sono: any[];
  humor: any | null;
  ocorrencias: any[];
  observacoes_gerais: string;
}
interface DiaResp {
  data: string;
  turma: { id: number; nome: string };
  criancas: CriancaDia[];
}

const hoje = () => new Date().toISOString().slice(0, 10);

// Ações rápidas por categoria (um toque = um registro).
const ALIMENTACAO = [
  { label: 'Comeu tudo', ev: { status: 'tudo' }, cor: '#2e7d32' },
  { label: 'Metade', ev: { status: 'metade' }, cor: '#f9a825' },
  { label: 'Pouco', ev: { status: 'pouco' }, cor: '#ef6c00' },
  { label: 'Recusou', ev: { status: 'recusou' }, cor: '#c62828' },
];
const HIGIENE = [
  { label: '💧 Xixi', ev: { tipo: 'xixi' } },
  { label: '💩 Cocô', ev: { tipo: 'cocô' } },
  { label: '🧷 Troca seca', ev: { tipo: 'troca' } },
  { label: '🛁 Banho', ev: { tipo: 'banho' } },
];
const HUMOR = [
  { label: '😀', ev: { escala: 5, classificacao: 'bem' } },
  { label: '🙂', ev: { escala: 4, classificacao: 'tranquilo' } },
  { label: '😐', ev: { escala: 3, classificacao: 'neutro' } },
  { label: '😢', ev: { escala: 2, classificacao: 'sensível' } },
];

export default function RotinaPage() {
  const { turmas } = useTurmas();
  const [data, setData] = useState(hoje());
  const [idTurma, setIdTurma] = useState<number>(0);
  const [dia, setDia] = useState<DiaResp | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [salvandoId, setSalvandoId] = useState<string>('');

  // Seleciona a primeira turma disponível assim que a lista chegar.
  useEffect(() => {
    if (!idTurma && turmas.length) setIdTurma(turmas[0].id);
  }, [turmas, idTurma]);

  const carregar = useCallback(async () => {
    if (!idTurma) return;
    setCarregando(true);
    setErro('');
    try {
      const { data: d } = await api.get<DiaResp>(`/rotina/dia`, { params: { data, id_turma: idTurma } });
      setDia(d);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível carregar o dia.');
      setDia(null);
    } finally {
      setCarregando(false);
    }
  }, [data, idTurma]);

  useEffect(() => { carregar(); }, [carregar]);

  const registrar = async (c: CriancaDia, categoria: string, evento: any) => {
    setSalvandoId(`${c.id_aluno}:${categoria}`);
    try {
      await api.post('/rotina/registro-rapido', { id_aluno: c.id_aluno, id_turma: idTurma, data, categoria, evento });
      await carregar();
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Erro ao registrar.');
    } finally {
      setSalvandoId('');
    }
  };

  const remover = async (c: CriancaDia, categoria: string, index: number) => {
    try {
      await api.post('/rotina/remover-evento', { id_aluno: c.id_aluno, data, categoria, index });
      await carregar();
    } catch { /* silencioso */ }
  };

  const ocorrencia = async (c: CriancaDia) => {
    const texto = window.prompt(`Ocorrência de ${c.nome}:`);
    if (texto && texto.trim()) await registrar(c, 'ocorrencias', { descricao: texto.trim() });
  };

  const salvarObs = async (c: CriancaDia, texto: string) => {
    if (texto === c.observacoes_gerais) return;
    await registrar(c, 'observacoes_gerais', texto);
  };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: '0 0 2px' }}>📔 Rotina do dia</h2>
          <div style={{ fontSize: 13, color: '#888' }}>Lançamento rápido — um toque por registro.</div>
        </div>
        <label style={{ marginLeft: 'auto' }}>Turma<br />
          <select value={idTurma} onChange={(e) => setIdTurma(Number(e.target.value))} style={{ padding: 6 }}>
            {turmas.length === 0 && <option value={0}>—</option>}
            {turmas.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
          </select>
        </label>
        <label>Data<br />
          <input type="date" value={data} onChange={(e) => setData(e.target.value)} style={{ padding: 6 }} />
        </label>
        <button onClick={carregar} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #ddd', background: '#fafafa', cursor: 'pointer' }}>↻</button>
      </div>

      {erro && <div style={{ color: '#c62828', fontSize: 14, marginBottom: 10 }}>{erro}</div>}
      {carregando && <div style={{ color: '#888' }}>Carregando…</div>}

      {dia && dia.criancas.length === 0 && !carregando && (
        <div style={{ color: '#888' }}>Nenhuma criança ativa nesta turma.</div>
      )}

      {dia && dia.criancas.map((c) => {
        const saving = (cat: string) => salvandoId === `${c.id_aluno}:${cat}`;
        return (
          <div key={c.id_aluno} style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 14, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#ffe0ec', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🧒</div>
              <strong style={{ fontSize: 16 }}>{c.nome}</strong>
              {c.humor && <span style={{ marginLeft: 'auto', fontSize: 22 }} title={c.humor.classificacao}>{HUMOR.find((h) => h.ev.escala === c.humor.escala)?.label || '🙂'}</span>}
            </div>

            <LinhaAcao titulo="🍽️ Alimentação" saving={saving('alimentacao')}>
              {ALIMENTACAO.map((a) => <Btn key={a.label} cor={a.cor} onClick={() => registrar(c, 'alimentacao', a.ev)}>{a.label}</Btn>)}
              <Chips itens={c.alimentacao} rotulo={(e) => e.status} onRemove={(i) => remover(c, 'alimentacao', i)} />
            </LinhaAcao>

            <LinhaAcao titulo="🧷 Higiene / Fraldas" saving={saving('higiene')}>
              {HIGIENE.map((h) => <Btn key={h.label} onClick={() => registrar(c, 'higiene', h.ev)}>{h.label}</Btn>)}
              <Btn cor="#c62828" onClick={() => registrar(c, 'higiene', { tipo: 'troca', acidente: true })}>⚠️ Acidente</Btn>
              <Chips itens={c.higiene} rotulo={(e) => `${e.tipo}${e.acidente ? '⚠️' : ''}`} onRemove={(i) => remover(c, 'higiene', i)} />
            </LinhaAcao>

            <LinhaAcao titulo="😴 Sono" saving={saving('sono')}>
              <Btn onClick={() => registrar(c, 'sono', { evento: 'dormiu' })}>😴 Dormiu</Btn>
              <Btn onClick={() => registrar(c, 'sono', { evento: 'acordou' })}>☀️ Acordou</Btn>
              <Chips itens={c.sono} rotulo={(e) => `${e.evento} ${e.hora || ''}`} onRemove={(i) => remover(c, 'sono', i)} />
            </LinhaAcao>

            <LinhaAcao titulo="🙂 Humor" saving={saving('humor')}>
              {HUMOR.map((h) => <Btn key={h.label} onClick={() => registrar(c, 'humor', h.ev)}>{h.label}</Btn>)}
            </LinhaAcao>

            <LinhaAcao titulo="⚠️ Ocorrências" saving={saving('ocorrencias')}>
              <Btn cor="#e65100" onClick={() => ocorrencia(c)}>+ Registrar ocorrência</Btn>
              <Chips itens={c.ocorrencias} rotulo={(e) => e.descricao} onRemove={(i) => remover(c, 'ocorrencias', i)} />
            </LinhaAcao>

            <label style={{ display: 'block', fontSize: 13, color: '#666', marginTop: 6 }}>📝 Observações do dia
              <input
                defaultValue={c.observacoes_gerais}
                onBlur={(e) => salvarObs(c, e.target.value)}
                placeholder="Recado para a família…"
                style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid #ddd', borderRadius: 8 }}
              />
            </label>
          </div>
        );
      })}
    </div>
  );
}

function LinhaAcao({ titulo, saving, children }: { titulo: string; saving?: boolean; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', padding: '6px 0', borderTop: '1px dashed #f0f0f0' }}>
      <span style={{ flex: '0 0 150px', fontSize: 13, fontWeight: 600, color: '#555', opacity: saving ? 0.5 : 1 }}>{titulo}</span>
      {children}
    </div>
  );
}

function Btn({ children, onClick, cor = '#1565c0' }: { children: React.ReactNode; onClick: () => void; cor?: string }) {
  return (
    <button onClick={onClick} style={{ padding: '5px 10px', borderRadius: 16, border: `1px solid ${cor}`, background: '#fff', color: cor, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
      {children}
    </button>
  );
}

function Chips({ itens, rotulo, onRemove }: { itens: any[]; rotulo: (e: any) => string; onRemove: (i: number) => void }) {
  if (!itens?.length) return null;
  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap', marginLeft: 4 }}>
      {itens.map((e, i) => (
        <span key={i} style={{ background: '#eef2f7', borderRadius: 12, fontSize: 12, padding: '2px 6px', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {e.hora ? `${e.hora} ` : ''}{rotulo(e)}
          <button onClick={() => onRemove(i)} title="Desfazer" style={{ border: 'none', background: 'transparent', color: '#c62828', cursor: 'pointer', padding: 0, lineHeight: 1 }}>✕</button>
        </span>
      ))}
    </span>
  );
}
